import { handleError } from '../../utils/errorHandler';
/**
 * Robust Retry Handler with Exponential Backoff
 * Solves cold start issues definitively
 */

export interface RetryConfig {
  maxRetries: number;
  initialDelay: number;
  maxDelay: number;
  factor: number;
  shouldRetry?: (error: unknown) => boolean;
  onRetry?: (attempt: number, delay: number, error: unknown) => void;
}

export class RetryHandler {
  private static defaultConfig: RetryConfig = {
    maxRetries: 3,
    initialDelay: 1000, // 1 second
    maxDelay: 10000, // 10 seconds
    factor: 2,
    shouldRetry: (error: unknown) => {
      // Retry on network errors, timeouts, and 503 (service unavailable)
      const axiosError = error as {
        response?: { status?: number; data?: { error_code?: string } };
      };
      if (!axiosError.response) return true; // Network error

      // DON'T retry configuration errors - they require user action
      // These have error_code like FOOOCUS_NOT_RUNNING, FOOOCUS_NOT_CONFIGURED, etc.
      if (axiosError.response?.data?.error_code) {
        return false;
      }

      const status = axiosError.response?.status;
      return status === 503 || status === 502 || status === 504 || status === 0;
    },
    onRetry: (attempt: number, delay: number, error: unknown) => {
      const errorMessage = handleError(error, 'RetryHandler', 'Unknown error');
      console.log(`[Retry] Attempt ${attempt}, waiting ${delay}ms. Error: ${errorMessage}`);
    },
  };

  /**
   * Execute a function with exponential backoff retry
   */
  static async withRetry<T>(fn: () => Promise<T>, config?: Partial<RetryConfig>): Promise<T> {
    const mergedConfig = { ...this.defaultConfig, ...config };
    let lastError: unknown;

    for (let attempt = 1; attempt <= mergedConfig.maxRetries; attempt++) {
      try {
        return await fn();
      } catch (error: unknown) {
        lastError = error;

        // Check if we should retry
        if (!mergedConfig.shouldRetry!(error)) {
          throw error;
        }

        // Check if we have more attempts
        if (attempt === mergedConfig.maxRetries) {
          console.error(`[Retry] All ${mergedConfig.maxRetries} attempts failed`);
          throw error;
        }

        // Calculate delay with exponential backoff
        const delay = Math.min(
          mergedConfig.initialDelay * Math.pow(mergedConfig.factor, attempt - 1),
          mergedConfig.maxDelay
        );

        // Add jitter to prevent thundering herd
        const jitteredDelay = delay + Math.random() * 500;

        // Call retry callback
        mergedConfig.onRetry!(attempt, jitteredDelay, error);

        // Wait before retrying
        await this.sleep(jitteredDelay);
      }
    }

    throw lastError;
  }

  /**
   * Special configuration for cold start scenarios
   */
  static getColdStartConfig(): Partial<RetryConfig> {
    return {
      maxRetries: 5, // More retries for cold starts
      initialDelay: 2000, // Start with 2 seconds
      maxDelay: 15000, // Up to 15 seconds
      factor: 1.5, // Slower increase
      shouldRetry: (error: unknown) => {
        // Always retry on connection errors during cold start
        const axiosError = error as {
          response?: { status?: number; data?: { error_code?: string } };
          message?: string;
        };
        if (!axiosError.response) return true;

        // DON'T retry configuration errors - they require user action
        if (axiosError.response?.data?.error_code) {
          return false;
        }

        const status = axiosError.response?.status;
        const message = axiosError.message?.toLowerCase() || '';

        // Retry on cold start indicators
        return (
          status === 503 || // Service Unavailable
          status === 502 || // Bad Gateway
          status === 504 || // Gateway Timeout
          status === 0 || // Network error
          message.includes('cold start') ||
          message.includes('service starting') ||
          message.includes('timeout') ||
          message.includes('econnrefused') ||
          message.includes('econnaborted')
        );
      },
      onRetry: (attempt: number, delay: number) => {
        // User-friendly messages during cold start
        if (attempt === 1) {
          console.log('🚀 Service is starting up, this may take a moment...');
        } else if (attempt === 2) {
          console.log('⏳ Almost ready, warming up the service...');
        } else {
          console.log(`🔄 Retry ${attempt}: Waiting ${Math.round(delay / 1000)}s...`);
        }
      },
    };
  }

  /**
   * Sleep utility
   */
  private static sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Preemptive warmup for cold starts
   * Call this immediately when the app loads
   */
  static async warmupService(
    healthCheckFn: () => Promise<unknown>,
    config?: Partial<RetryConfig>
  ): Promise<boolean> {
    // Skip warmup in test environment to prevent network calls
    if (typeof import.meta !== 'undefined' && import.meta.env?.MODE === 'test') {
      console.log('[Warmup] Skipped in test environment');
      return true;
    }

    const warmupConfig = {
      ...this.getColdStartConfig(),
      ...config,
      onRetry: (attempt: number) => {
        console.log(`[Warmup] Attempt ${attempt}: Service is starting...`);
      },
    };

    try {
      await this.withRetry(healthCheckFn, warmupConfig);
      console.log('✅ Service is ready and warm!');
      return true;
    } catch {
      console.warn('⚠️ Service warmup failed, but app will retry on first request');
      return false;
    }
  }
}

/**
 * Circuit Breaker to prevent overwhelming a failing service
 */
export class CircuitBreaker {
  private failureCount = 0;
  private lastFailureTime = 0;
  private state: 'closed' | 'open' | 'half-open' = 'closed';

  private threshold = 5;
  private timeout = 60000; // 1 minute

  constructor(
    threshold = 5,
    timeout = 60000 // 1 minute
  ) {
    this.threshold = threshold;
    this.timeout = timeout;
  }

  async execute<T>(fn: () => Promise<T>): Promise<T> {
    // Check if circuit is open
    if (this.state === 'open') {
      const now = Date.now();
      if (now - this.lastFailureTime > this.timeout) {
        this.state = 'half-open';
        console.log('[Circuit Breaker] Trying half-open state');
      } else {
        throw new Error('Circuit breaker is OPEN - service is temporarily unavailable');
      }
    }

    try {
      const result = await fn();

      // Success - reset on half-open or reduce failure count
      if (this.state === 'half-open') {
        this.state = 'closed';
        this.failureCount = 0;
        console.log('[Circuit Breaker] Circuit CLOSED - service recovered');
      } else if (this.failureCount > 0) {
        this.failureCount--;
      }

      return result;
    } catch (error) {
      this.failureCount++;
      this.lastFailureTime = Date.now();

      if (this.failureCount >= this.threshold) {
        this.state = 'open';
        console.error(`[Circuit Breaker] Circuit OPEN after ${this.failureCount} failures`);
      }

      throw error;
    }
  }

  reset(): void {
    this.failureCount = 0;
    this.state = 'closed';
    this.lastFailureTime = 0;
  }
}
